import type { ExternalSiIdentifier } from 'src/app/models/traceability-state.model';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, finalize, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EventStateService } from '../state-service/event-state.service';
import { LOCALLY_HANDLED_HTTP_ERROR } from '../../constants/http-context.const';

export type SecretKeyError = 'invalid' | 'expired';

@Injectable({
  providedIn: 'root'
})
export class ExternalService {
  private readonly http = inject(HttpClient);
  private readonly eventStateService = inject(EventStateService);

  getVerifySiNumbSecretKey(
    si_numb: ExternalSiIdentifier,
    sidetail_id: ExternalSiIdentifier,
    secret_key: string
  ) {
    this.eventStateService.isVerifyingSecretKeyLoading = true;
    return this.http
      .get<{ valid: boolean; reason?: SecretKeyError }>(
        `${environment.TRACEABILITY_EXTERNAL}secret-key/verify/?si=${si_numb}&secret_key=${secret_key}&sidetail_id=${sidetail_id}`,
        {
          context: new HttpContext().set(
            LOCALLY_HANDLED_HTTP_ERROR,
            (error) => this.getSecretKeyError(error, sidetail_id) !== undefined
          )
        }
      )
      .pipe(
        catchError((error: unknown) => {
          const reason = this.getSecretKeyError(error, sidetail_id);
          return reason
            ? of({ valid: false, reason })
            : throwError(() => error);
        }),
        finalize(() => {
          this.eventStateService.isVerifyingSecretKeyLoading = false;
        })
      );
  }

  private getSecretKeyError(
    error: unknown,
    siDetailId: ExternalSiIdentifier
  ): SecretKeyError | undefined {
    if (!(error instanceof HttpErrorResponse) || error.status !== 400) {
      return undefined;
    }
    const body: unknown = error.error;
    if (!body || typeof body !== 'object' || !('detail' in body)) {
      return undefined;
    }
    if (body.detail === 'Invalid SI secret key.') {
      return 'invalid';
    }
    if (
      body.detail ===
      `The secret key for this SI ${siDetailId} has expired. Please ask the sender to resend.`
    ) {
      return 'expired';
    }
    return undefined;
  }

  downloadGeoJSONFiles(
    si_numb: ExternalSiIdentifier,
    sidetail_id: ExternalSiIdentifier
  ) {
    this.eventStateService.isDownloadingExternalJsonFileLoading = true;
    return this.http
      .get(
        `${environment.TRACEABILITY_EXTERNAL}plantations/download-euis/?si=${si_numb}&sidetail_id=${sidetail_id}`,
        { responseType: 'blob' }
      )
      .pipe(
        finalize(() => {
          this.eventStateService.isDownloadingExternalJsonFileLoading = false;
        })
      );
  }

  downloadEudrPackage(
    si_numb: ExternalSiIdentifier,
    sidetail_id: ExternalSiIdentifier
  ) {
    this.eventStateService.isDownloadingExternalZipFileLoading = true;
    return this.http
      .get(
        `${environment.TRACEABILITY_EXTERNAL}download-eudr-package/?si=${si_numb}&sidetail_id&sidetail_id=${sidetail_id}`,
        { responseType: 'blob' }
      )
      .pipe(
        finalize(() => {
          this.eventStateService.isDownloadingExternalZipFileLoading = false;
        })
      );
  }
}
